import { saveAs } from "file-saver";
import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { getCurrentWeekRange, myAxios, post_max_size, round } from "src/components/common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";

import { extraFields } from "./PurchaseList";
import { fields } from "./PurchaseFilterForm";

export async function purchasesLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    const week = getCurrentWeekRange();
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if (![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("poDateFrom")) {
        url.searchParams.set("poDateFrom", week.start);
        shouldRedirect = true;
    }
    if (!url.searchParams.get("poDateTo")) {
        url.searchParams.set("poDateTo", week.end);
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }
    const formData = new FormData();
    formData.append("page", url.searchParams.get("page")!);
    formData.append("limit", url.searchParams.get("limit")!);
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({ fileName: "purchases/readPurchases.php", formData });
    if (!res) throw redirect("..");
    return res.data;
}

export async function purchaseLoader({ params }: LoaderFunctionArgs) {
    const { id } = params;
    if (!id) {
        throw redirect("/Purchases");
    }
    const formData = new FormData();
    formData.append("id", id);
    const res = await myAxios({ fileName: "purchases/readPurchase.php", formData });
    if (!res || !res.data) throw redirect("..");
    return {
        purchase: res.data,
    };
}

const urlMap = {
    Add: "purchases/createPurchase.php",
    Edit: "purchases/updatePurchase.php",
};

function validatePdfUpload(formData: FormData, fieldName: "quoteFile" | "receiptFile", label: string) {
    const value = formData.get(fieldName);
    if (!(value instanceof File) || !value.size) return true;
    const normalizedType = value.type.toLowerCase();
    const normalizedName = value.name.toLowerCase();
    const isPdf = normalizedType === "application/pdf" || normalizedName.endsWith(".pdf");
    if (!isPdf) {
        window.alert(`${label} must be a PDF.`);
        return false;
    }
    if (value.size > post_max_size) {
        const uploadSizeInM = round(value.size / 1024 / 1024, 2);
        const maxSizeInM = round(post_max_size / 1024 / 1024);
        window.alert(`${label} is too large (${uploadSizeInM}MB). Max allowed size is ${maxSizeInM}MB.`);
        return false;
    }
    return true;
}

export async function purchaseAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    const action = formData.get("action") as keyof typeof urlMap;
    const url = urlMap[action];
    if (!url) {
        window.alert("Invalid purchase action.");
        return null;
    }
    if (!formData.get("poNumber")) {
        window.alert("PO number is required.");
        return null;
    }
    if (!formData.get("requesterId")) {
        window.alert("Please select requester.");
        return null;
    }
    if (!formData.get("approverId")) {
        window.alert("Please select approver.");
        return null;
    }
    if (!validatePdfUpload(formData, "quoteFile", "Quote file")) {
        return null;
    }
    if (!validatePdfUpload(formData, "receiptFile", "Receipt file")) {
        return null;
    }
    const duplicateCheck = new FormData();
    duplicateCheck.append("poNumber", String(formData.get("poNumber")));
    if (formData.get("id")) {
        duplicateCheck.append("id", String(formData.get("id")));
    }
    const duplicateRes = await myAxios({
        fileName: "purchases/checkDuplicatePO.php",
        formData: duplicateCheck,
        signal: controller.signal,
    });
    if (!duplicateRes) return null;
    if (duplicateRes.data?.id) {
        window.alert(`PO number duplicated with ${duplicateRes.data.poNumber}.`);
        return null;
    }
    const lines = JSON.parse(String(formData.get("data") ?? "[]")) as { description?: string }[];
    if (!Array.isArray(lines) || !lines.length || !lines.some(line => (line.description ?? "").trim())) {
        window.alert("At least one line item is required.");
        return null;
    }
    const res = await myAxios({ fileName: url, formData, signal: controller.signal });
    if (!res) return null;
    if (action === "Add") {
        return redirect(`/Purchases/${res.data.id}`);
    }
    return redirect("..");
}

async function runSimpleAction(request: Request, fileName: string, fallback = "..") {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName,
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect(fallback);
}

export async function deletePurchaseAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "purchases/deletePurchase.php", "../..");
    throw redirect("../..");
}

export async function voidPurchaseAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "purchases/voidPurchase.php");
    throw redirect("..");
}

export async function validatePurchaseAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "purchases/validatePurchase.php");
    throw redirect("..");
}

export async function notifyPurchaseAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "purchases/notifyPurchase.php");
    throw redirect("..");
}

export async function approvePurchaseAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "purchases/approvePurchase.php");
    throw redirect("..");
}

export async function paidPurchaseAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName: "purchases/paidPurchase.php",
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);
    throw redirect(`..?${url.searchParams.toString()}`);
}

export async function purchaseExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: "purchases/exportPurchases.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) throw redirect(`..?${url.searchParams.toString()}`);
    const disposition = res.headers["content-disposition"];
    let filename = "Purchases.csv";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(new Blob([res.data]), filename);
    throw redirect(`..?${url.searchParams.toString()}`);
}
